import React, { useEffect, useMemo, useState } from "react";
import "react-toastify/dist/ReactToastify.css";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { ToastContainer } from "react-toastify";
import moment from "moment";
import { CountTotalResponse, getCountInOneDay } from "../../../api/order.api";
import { Category, Menu, getAllCatagory } from "../../../api/category.api";
import { State } from "../../../reducer";
import { getCafeStatus } from "../../../api/cafe.api";
import { User } from "../../../interfaces/user";
import { canBypassClosure } from "../../../utils/access";
import { useTheme } from "../../../theme/useTheme";
import { HalloweenArt, HarvestBanner } from "../../../theme/HalloweenArt";
import { ikThumb } from "../../../utils/image";

const MenuCardSkeleton: React.FC = () => (
  <div className="row-v2 animate-pulse">
    <div className="w-[64px] h-[64px] rounded-2xl bg-ink-100" />
    <div className="flex-1 space-y-2">
      <div className="h-[16px] bg-ink-200 rounded w-2/3" />
      <div className="h-3 bg-ink-100 rounded w-1/2" />
    </div>
    <div className="w-[36px] h-[36px] rounded-full bg-ink-100" />
  </div>
);

const MenuList: React.FC = () => {
  const user = useSelector((state: State) => state.user);
  const [categories, setCategories] = useState<Category[]>([]);
  const [selected, setSelected] = useState<number | null>(null);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [countOrders, setCountOrder] = useState<CountTotalResponse>();
  const [friday, setFriday] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isTabLoading, setIsTabLoading] = useState(false);

  const bypassAll = canBypassClosure(user?.role);
  const dailyLimit = countOrders?.limit ?? 2;
  const orderLimitReached = !bypassAll && countOrders && countOrders.totalOrder >= dailyLimit;
  const fridayBlocked = friday && !bypassAll;

  const handleTabChange = (categoryId: number) => {
    setIsTabLoading(true);
    setSelected(categoryId);
    setTimeout(() => setIsTabLoading(false), 200);
  };

  const menus = useMemo<Menu[]>(() => {
    if (selected === null) return [];
    const selectedCategory = categories.find((e) => e.id === selected);
    if (!selectedCategory) return [];
    return selectedCategory.menus.filter((item) => item.isAvailable === "available");
  }, [categories, selected]);

  const storeRudux = (response: User) => {
    dispatch({ type: "LOGIN", payload: response });
  };

  useEffect(() => {
    getCafeStatus().then((result) => {
      const responsepost: User = {
        login: true,
        sub: "auth_google",
        id: user?.id,
        email: user?.email,
        name: user?.name,
        role: user?.role,
        isBarOpen: result?.isBarOpen,
        isCafeOpen: result?.isCafeOpen,
      };
      const bypass = canBypassClosure(user?.role);
      if (!result?.isCafeOpen && !result?.isBarOpen && !bypass) {
        return navigate(user?.role === "admin" ? "/close-all-admin" : "/close-all");
      } else if (!result?.isCafeOpen && result?.isBarOpen && !bypass) {
        return navigate("/order_bar");
      }
      storeRudux(responsepost);
    });
    getAllCatagory().then((Categorie) => {
      const filtered = Categorie.filter(
        (item) => item.categoryFor === "cafe" && item.isAvailable === "available"
      );
      setCategories(filtered);
      if (filtered.length > 0) setSelected(filtered[0].id);
      getCountInOneDay().then((countOrder) => {
        setCountOrder(countOrder);
        setIsLoading(false);
      });
    });
    const today = new Date();
    const day = moment(today).format("ddd : HH");
    if (day.split(":")[0] === "Fri" && +day.split(":")[1] >= 12) {
      setFriday(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSelectMenu = (menuID: number, menuName: string) => {
    dispatch({
      type: "SETMENU",
      payload: {
        menu_id: menuID,
        menu_name: menuName,
        type: "",
        sweetness: 0,
        remark: "",
        tumblerImage: "",
      },
      title: "Americano",
    });
    const isStaff = user?.role === "admin" || user?.role === "dev";
    navigate(isStaff ? `/form-admin/${menuName}/${menuID}` : `/form/${menuName}/${menuID}`);
  };

  const onMenuClick = (menu: Menu) => {
    if (menu.isActive !== "active" || fridayBlocked) return;
    if (orderLimitReached) {
      alert("You already reached your order limit!");
      return;
    }
    handleSelectMenu(menu.id, menu.name);
  };

  const [theme] = useTheme();
  const greeting = (() => {
    if (theme === "christmas") return "Merry Christmas";
    if (theme === "cny") return "Gōng xǐ fā cái";
    const h = new Date().getHours();
    if (h < 12) return "It's a great morning for coffee";
    if (h < 17) return "Good afternoon";
    return "Good evening";
  })();
  const greetingEmoji =
    theme === "christmas" ? "🎄" :
    theme === "cny" ? "🧧" :
    "☕";
  const firstName = user?.name?.split(" ")[0] ?? "";
  const heading =
    theme === "christmas"
      ? (firstName ? `Merry Christmas, ${firstName}` : "Merry Christmas")
      : theme === "cny"
        ? (firstName ? `新年快乐, ${firstName}` : "新年快乐")
        : (firstName ? `Good day, ${firstName}` : greeting);

  return (
    <div className="cafe-menu min-h-screen bg-white font-sans pb-[96px]">
      {/* Hero — Starbucks "Good morning, [name] ☕" pattern */}
      <header className="px-5 pt-[24px] pb-[16px]">
        <h1 className="font-display font-black text-[28px] leading-[1.15] tracking-tightest text-ink-1000">
          {heading} <span aria-hidden>{greetingEmoji}</span>
        </h1>

        {/* Quick actions row — Starbucks "Inbox · Stores" pattern */}
        <div className="flex items-center justify-between mt-4 -mx-1">
          <div className="flex items-center gap-5 px-1">
            <button
              type="button"
              onClick={() => navigate(user?.role === "admin" ? "/statusOrder-admin" : "/statusOrder")}
              className="flex items-center gap-1.5 text-sm font-medium text-ink-700 hover:text-ink-1000"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="1.8" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"/>
              </svg>
              Queue
            </button>
            <button
              type="button"
              onClick={() => navigate("/ordered")}
              className="flex items-center gap-1.5 text-sm font-medium text-ink-700 hover:text-ink-1000"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="1.8" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"/>
              </svg>
              History
            </button>
          </div>
          <div
            className={`inline-flex items-center gap-1.5 px-3 h-[30px] rounded-full text-xs font-bold ${
              orderLimitReached ? "bg-accent text-accent-on" : "bg-ink-100 text-ink-900"
            }`}
          >
            {countOrders?.totalOrder ?? 0}/{dailyLimit} today
          </div>
        </div>
      </header>

      <HarvestBanner />
      <div className="border-b border-ink-100" />

      {/* Hero card — when limit hit OR friday */}
      {(orderLimitReached || fridayBlocked) && (
        <div className="px-5 pt-[20px]">
          <div className="rounded-3xl bg-hero text-hero-on p-6 relative overflow-hidden">
            <div className="absolute -right-4 -top-4 w-[140px] h-[140px] rounded-full" style={{ background: "var(--festive-1)", opacity: "var(--decor-opacity)" }} />
            <div className="absolute -right-8 top-12 w-[100px] h-[100px] rounded-full" style={{ background: "var(--festive-2)", opacity: "var(--decor-opacity)" }} />
            <div className="relative">
              <p className="eyebrow-v2 text-hero-muted mb-2">{fridayBlocked ? "Heads up" : "All done"}</p>
              <h2 className="font-display font-black text-[22px] leading-tight tracking-tight">
                {fridayBlocked ? "Bar closes at noon on Fridays" : "You've reached today's limit"}
              </h2>
              <p className="text-sm text-hero-muted mt-1.5">
                {fridayBlocked ? "Order before 12:00." : "Come back tomorrow for more."}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Underline tabs — Starbucks "Scan & pay | Scan only" pattern */}
      <div className="menu-tabs sticky top-0 z-10 bg-white/95 backdrop-blur-md border-b border-ink-100">
        <div className="px-5 overflow-x-auto scrollbar-hide">
          <div className="flex gap-7 pt-4">
            {categories.filter((_, d) => d < 6).map((cat) => (
              <button
                key={cat.id}
                onClick={() => handleTabChange(cat.id)}
                className={`whitespace-nowrap ${cat.id === selected ? "tab-v2-active" : "tab-v2"}`}
              >
                {cat.name}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Section title */}
      <div className="px-5 pt-[24px] pb-[8px] flex items-end justify-between">
        <h2 className="font-display font-black text-[20px] tracking-tight text-ink-1000">
          {categories.find((c) => c.id === selected)?.name ?? "Menu"}
        </h2>
        <div className="flex items-center gap-2"><HalloweenArt variant="witch-hat" className="art-category" /><span className="text-xs text-ink-500 font-medium">{menus.length} items</span></div>
      </div>

      {/* List — divider rows, no card */}
      <main className="menu-grid px-5">
        {isLoading || isTabLoading ? (
          Array.from({ length: 6 }).map((_, i) => <MenuCardSkeleton key={i} />)
        ) : menus.length === 0 ? (
          <div className="py-[64px] flex flex-col items-center justify-center text-center">
            <div className="w-[56px] h-[56px] rounded-full bg-ink-100 flex items-center justify-center mb-4">
              <svg className="w-6 h-6 text-ink-400" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
              </svg>
            </div>
            <p className="font-display font-black text-lg text-ink-1000">No menu available</p>
            <p className="text-sm text-ink-500 mt-1">Try another category</p>
          </div>
        ) : (
          menus.map((menu) => {
            const disabled = menu.isActive !== "active" || fridayBlocked || orderLimitReached;
            return (
              <button
                key={menu.id}
                onClick={() => onMenuClick(menu)}
                disabled={menu.isActive !== "active" || fridayBlocked}
                className={`row-v2 w-full text-left ${disabled ? "opacity-40" : "active:bg-ink-50"}`}
              >
                <div className="flex-shrink-0 w-[64px] h-[64px] rounded-2xl bg-ink-100 flex items-center justify-center overflow-hidden">
                  {menu.image ? (
                    <img src={ikThumb(menu.image, 200, 200)} alt={menu.name} className="w-full h-full object-cover" loading="lazy" />
                  ) : (
                    <span className="font-display font-black text-2xl text-ink-300">
                      {menu.name.slice(0, 1).toUpperCase()}
                    </span>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-display font-bold text-base text-ink-1000 truncate">{menu.name}</div>
                  {menu.description && (
                    <div className="text-sm text-ink-500 mt-0.5 line-clamp-1">{menu.description}</div>
                  )}
                </div>
                <div
                  className={`flex-shrink-0 w-[36px] h-[36px] rounded-full flex items-center justify-center ${
                    disabled ? "bg-ink-100 text-ink-300" : "bg-accent text-accent-on"
                  }`}
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4"/>
                  </svg>
                </div>
              </button>
            );
          })
        )}
      </main>

      <ToastContainer />
    </div>
  );
};

export default MenuList;
